import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { isEqual } from '@deltek/specification-client/core/Utilities/objectUtils'
import React, {
    FC,
    MutableRefObject,
    memo,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { Cell, SortingRule } from 'react-table'

import { useLazyQuery } from '@apollo/client'
import { AuthContext } from '@deltek/specification-client/core/Configurations/AuthContext'
import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Button, Paper, Theme, Typography, makeStyles } from '@material-ui/core'
import {
    getBPMProductListingsHistory,
    getProductListingsHistory,
} from 'bpm/api/graphQL/queries/productHistory.queries'
import { SpecTable } from 'bpm/components/SpecTable'
import { ActionColumnProps } from 'bpm/components/SpecTable/Spectable.types'
import CustomFilterSelect from 'bpm/components/SpecTable/filters/CustomFilterSelectText'
import useSpecTableOptions from 'bpm/components/SpecTable/useSpecTableOptions'
import tableCell from 'bpm/pages/ProductCardManagementPage/ProductListingsPage/CellRenderers/tableCell'
import tableFilters from 'bpm/pages/ProductCardManagementPage/ProductListingsPage/CellRenderers/tableFilters'
import tableFormattedText from 'bpm/pages/ProductCardManagementPage/ProductListingsPage/CellRenderers/tableFormattedText'
import { noteTypes } from 'bpm/pages/ProductsPage/ProductDetailsPage/components/ListingNotes/ListingNotes.constants'
import { resetPDNStore } from 'bpm/pages/ProductsPage/ProductDetailsPage/components/ListingNotes/ListingNotes.store'
import ListingNotesModal from 'bpm/pages/ProductsPage/ProductDetailsPage/components/ListingNotes/ListingNotesModal'
import usePDNFiltersHook from 'bpm/pages/ProductsPage/ProductDetailsPage/components/ListingNotes/hooks/usePDNFiltersHook'
import usePDNhook from 'bpm/pages/ProductsPage/ProductDetailsPage/components/ListingNotes/hooks/usePDNHook'
import {
    isDeltekUserorAdmin,
    isUserBPMAdmin,
} from 'bpm/pages/ProductsPage/productsUtils'
import {
    editContainerFontColor,
    tvBlueLinkButtonColor,
} from 'bpm/shared/bpmColors'
import { SubmissionState } from 'pages/components/ProductListingDialog/Grid/ProductListingResultGridItem'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'
import { prepareOrderByString } from '../..'
import { ColumnMapType, productHistoryColumns } from '../../tablePage'

export interface ProductHistoryTableProps {
    listingId: string
}

const processProductHistoryData = (data: any[]) => {
    return data.reduce(
        (
            acc: any,
            {
                listingId,
                isActive,
                action,
                user,
                productDetailNotes = [],
                timeStamp,
                type,
                submissionStatus,
            }: any
        ) => {
            const reason = productDetailNotes
                .map((note: any) => note.reason)
                .join(', ')

            acc.history.push({
                listingId,
                isActive,
                action,
                reason,
                timeStamp,
                note: !!productDetailNotes.length,
                type,
                user: user?.email ?? '',
                userFullName: user?.userFullName ?? '',
                submissionStatus,
                productDetailNotes,
            })

            if (productDetailNotes.length) {
                acc.notes = [
                    ...acc.notes,
                    ...productDetailNotes.map(
                        ({
                            reason,
                            productDetailNoteType,
                        }: {
                            reason: string
                            productDetailNoteType: string
                        }) => ({
                            reason,
                            noteType:
                                noteTypes[
                                    productDetailNoteType as keyof typeof noteTypes
                                ],
                        })
                    ),
                ]
            }
            return acc
        },
        {
            history: [],
            notes: [],
        }
    )
}

const useStyles = makeStyles((theme: Theme) => ({
    noteFilterPaper: {
        '& .MuiAutocomplete-groupLabel': {
            color: editContainerFontColor,
            fontSize: pxToRem(14),
            lineHeight: pxToRem(14),
            margin: theme.spacing(0.5, 0),
        },
        '& .MuiAutocomplete-groupUl': {
            fontSize: pxToRem(14),
        },
    },
    notesButton: {
        color: tvBlueLinkButtonColor,
        fontWeight: 'normal',
        padding: 0,
        '&:hover': {
            cursor: 'pointer',
            backgroundColor: 'transparent',
        },
    },
}))

const ProductHistoryTable: FC<ProductHistoryTableProps> = ({ listingId }) => {
    const classes = useStyles()
    const [sortBy, setSortBy] = useState<any>([])
    const [filters, setFilters] = useState<any>([])
    const [productHistory, setProductHistory] = useState<any>([])
    const [filteredHistory, setFilteredHistory] = useState<any>([])
    const [skip, setSkip] = useState(0)
    const [hasNextPage, setHasNextPage] = useState(false)
    const [selectedNotes, setSelectedNotes] = useState([])
    const [totalItems, setTotalItems] = useState(0)
    const [widths, setWidths] = useState<Record<string, number>>({})

    const [columns, setColumns] = useState<any[]>([])
    const [reasonObjects, setReasonObjects] = useState<any[]>([{}])
    const [showFiltersRow, setShowFiltersRow] = useState(false)
    const [isNotesDialogOpen, setIsNotesDialogOpen] = useState(false)
    const [listingLevel, setListingLevel] = useState<ListingLevel>()

    const currentUserInfoResponse = useContext(AuthContext)

    const { isAdminAccess, isBPMReviewer } = useMemo(() => {
        const {
            isDeltekAdmin,
            isDeltekPublishingAuthor,
            isDeltekAuthor,
            isDeltekReviewer,
        } = isDeltekUserorAdmin(currentUserInfoResponse)

        const { isBPMReviewer } = isUserBPMAdmin(currentUserInfoResponse)

        return {
            isBPMReviewer,
            isAdminAccess:
                isDeltekAdmin ||
                isDeltekPublishingAuthor ||
                isDeltekAuthor ||
                isDeltekReviewer,
        }
    }, [currentUserInfoResponse])

    const { clearFilters } = usePDNFiltersHook()

    const limit = 50
    const { showGlobalSnackbar } = useGlobalSnackbar()

    const divContainer = useRef<HTMLDivElement>()

    const prevSortBy: MutableRefObject<SortingRule<ColumnMapType>[]> = useRef(
        []
    )

    const { fetchNotes, pdnLoading } = usePDNhook()

    const [fetchHistory, { loading: isLoading }] = useLazyQuery(
        isAdminAccess
            ? getProductListingsHistory
            : getBPMProductListingsHistory,
        {
            onCompleted: (data) => {
                if (Object.keys(data?.getProductListingLogs ?? {}).length) {
                    const { history, notes } = processProductHistoryData(
                        data.getProductListingLogs.logs
                    )
                    if (isAdminAccess) {
                        const options = [
                            ...new Set([
                                ...reasonObjects.map(
                                    (note: Record<string, string>) =>
                                        JSON.stringify(note)
                                ),
                                ...notes.map((note: Record<string, string>) =>
                                    JSON.stringify(note)
                                ),
                            ]),
                        ]
                            .filter((note) => {
                                return note !== '{}'
                            })
                            .map((note: string) => JSON.parse(note))
                        setReasonObjects(options)
                    }

                    let tempHistory = [...productHistory, ...history]

                    if (isBPMReviewer) {
                        tempHistory = tempHistory.filter(
                            (row: ColumnMapType) =>
                                row.submissionStatus !==
                                SubmissionState.DEPRECATED
                        )
                    }

                    setProductHistory(tempHistory)
                    setSkip(data.getProductListingLogs.skip + limit)
                    setHasNextPage(
                        data.getProductListingLogs.totalResult > skip + limit
                    )
                    setTotalItems(data.getProductListingLogs?.totalResult)
                }
            },
            onError: () => {
                showGlobalSnackbar({
                    variables: {
                        message: 'Error fetching Product History',
                        variant: 'error',
                        id: 'ProductCardManagementTable_HistoryFetchError',
                    },
                })
            },
            fetchPolicy: 'network-only',
        }
    )

    const fetchData = useCallback(() => {
        fetchHistory({
            variables: {
                input: { listingId, skip, limit },
            },
        })
    }, [skip, listingId, limit])

    const stateCallback = useCallback((state) => {
        if (!isEqual(prevSortBy.current, state?.sortBy)) {
            setSortBy(state.sortBy)
            prevSortBy.current = state?.sortBy
        }

        setFilters?.(state.filters)
    }, [])

    const onFilterClick = useCallback(() => {
        setShowFiltersRow(!showFiltersRow)
    }, [showFiltersRow])

    const CustomPaper = memo(({ children, ...props }) => {
        return (
            <Paper {...props} className={classes.noteFilterPaper}>
                {children}
            </Paper>
        )
    })

    const actionColumnProps: ActionColumnProps<ColumnMapType> = {
        Cell: ({}) => {
            return <div data-testid="productHistoryActionColumn"></div>
        },
        width: 60,
    }

    const tableProps = useMemo(() => {
        return {
            actionColumnProps,
            allowGrouping: false,
            data: filteredHistory,
            columns,
            divContainerRef: divContainer,
            fetchData: fetchData,
            fetchDataLoading: isLoading,
            fetchNextData: fetchData,
            fetchNextDataLoading: isLoading,
            filters,
            height: window.innerHeight - 600,
            hasNextPage,
            onStateChange: stateCallback,
            sortBy,
            pageSize: 150,
            showCheckboxColumn: true,
            minWidth: 100,
            hideScroll: true,
            hideCheckbox: true,
            specTableTitleSettings: {
                theme: 'light',
                showTableSettings: false,
                onFilterClick,
                showFiltersRow,
                tableSettings: {
                    showPinning: false,
                    initialPinningProps: {
                        firstColumnPinned: false,
                        lastColumnPinned: false,
                    },
                },
            },
            totalItems,
        }
    }, [
        stateCallback,
        columns,
        reasonObjects,
        filteredHistory,
        isLoading,
        fetchData,
        filters,
        sortBy,
        hasNextPage,
        showFiltersRow,
        actionColumnProps,
    ])

    const specTableProps = useSpecTableOptions(tableProps)

    const filterRows = useCallback(
        (rows) => {
            return rows.filter((row: ColumnMapType) => {
                return filters.every((filter: { id: string; value: any }) => {
                    const keyName = filter?.id as keyof ColumnMapType

                    if (keyName === 'isActive') {
                        return (
                            !filter?.value?.length ||
                            filter?.value?.some((statusFilter: string) => {
                                if (statusFilter !== '') {
                                    if (
                                        (statusFilter === 'Active' &&
                                            !row[keyName]) ||
                                        (statusFilter === 'Inactive' &&
                                            row[keyName])
                                    ) {
                                        return false
                                    }
                                }
                                return true
                            })
                        )
                    } else if (
                        keyName === 'type' ||
                        keyName === 'submissionStatus'
                    ) {
                        return (
                            !filter?.value?.length ||
                            filter?.value?.some((statusFilter: string) => {
                                if (statusFilter !== '') {
                                    if (statusFilter !== row[keyName]) {
                                        return false
                                    }
                                }
                                return true
                            })
                        )
                    } else if (keyName === 'reason') {
                        return (
                            !filter?.value?.length ||
                            filter?.value?.some(
                                (statusFilter: {
                                    reason: string
                                    noteType: string
                                }) => {
                                    if (!!Object.keys(statusFilter)) {
                                        if (
                                            !row[keyName].includes(
                                                statusFilter.reason
                                            )
                                        ) {
                                            return false
                                        }
                                    }
                                    return true
                                }
                            )
                        )
                    } else if (keyName === 'timeStamp') {
                        if (
                            `${new Date(
                                row[keyName]
                            ).toLocaleDateString()}`.includes(
                                `${filter?.value}`
                            )
                        ) {
                            return true
                        }
                        return false
                    } else if (
                        // for text based
                        !(row[keyName] as string)
                            ?.toLowerCase()
                            .includes(filter?.value?.trim().toLowerCase())
                    ) {
                        return false
                    }

                    return true
                })
            })
        },
        [filters]
    )

    const handleCloseNotesModal = useCallback(() => {
        setIsNotesDialogOpen(false)
        setSelectedNotes([])
        resetPDNStore()
        clearFilters()
    }, [])

    const sortRows = useCallback(
        (rows) => {
            return rows.sort((a: any, b: any) => {
                for (const sort of sortBy) {
                    const sortId = sort.id as keyof typeof a
                    if (a[sortId] < b[sortId]) {
                        return sort.desc ? 1 : -1
                    }
                    if (a[sortId] > b[sortId]) {
                        return sort.desc ? -1 : 1
                    }
                }
                return 0
            })
        },
        [sortBy]
    )

    const updateList = useCallback(() => {
        let newRows = [...productHistory]
        newRows = filterRows(newRows)
        newRows = sortRows(newRows)
        setFilteredHistory(newRows)
    }, [productHistory, filterRows, sortRows])

    const calculateWidths = useCallback((width: number = 1350) => {
        // checkbox 48
        // actionColumn 60
        // timeStamp: 130,
        // user 250
        // isActive: 120,
        // submissionStatus: 200,
        // reason: 200,
        // type: 180,
        // note: 150,

        if (isAdminAccess) {
            setWidths({
                timeStamp: width * 0.1,
                user: width * 0.16,
                type: width * 0.14,
                submissionStatus: width * 0.14,
                isActive: width * 0.1,
                reason: width * 0.14,
                note: width * 0.12,
            })
        } else {
            setWidths({
                timeStamp: width * 0.12,
                user: width * 0.21,
                type: width * 0.29,
                submissionStatus: width * 0.16,
                isActive: width * 0.12,
            })
        }
    }, [])

    const NotesCell = ({ row }: Cell<ColumnMapType>) => {
        const value = row.original?.note
        const listingId = row.original?.listingId
        const listingType = row.original?.type
        if (value === true || value === false) {
            return (
                <HoverTooltip text={value ? 'Note' : ''}>
                    <Button
                        className={classes.notesButton}
                        onClick={() => {
                            fetchNotes(listingId)
                            setListingLevel(listingType)
                            setIsNotesDialogOpen(true)
                            setSelectedNotes(
                                row.original.productDetailNotes.map(
                                    (note: Record<string, string>) => note.id
                                )
                            )
                        }}
                    >
                        {value ? 'Note' : ''}
                    </Button>
                </HoverTooltip>
            )
        } else {
            return <></>
        }
    }

    const renderColumn = useCallback((column) => {
        let tempColumn = { ...column }

        if (!!tableFilters[column.id]) {
            tempColumn.Filter = tableFilters[column.id]
        }

        if (!!tableFormattedText[column.id]) {
            tempColumn.formattedText = tableFormattedText[column.id]
        }

        if (!!tableCell[column.id]) {
            tempColumn.Cell = tableCell[column.id]
        }

        if (column.id === 'hasProductDetailNotes')
            return {
                ...column,
                Cell: NotesCell,
                Filter: tableFilters[column.id],
            }

        return tempColumn
    }, [])

    useEffect(() => {
        if (!!Object.keys(widths).length) {
            setColumns([
                ...productHistoryColumns
                    .filter(
                        (col) =>
                            !(
                                !isAdminAccess &&
                                (col.id === 'note' || col.id === 'reason')
                            ) && !(isBPMReviewer && col.id === 'isActive')
                    )
                    .map((column) => {
                        if (column.id === 'note') {
                            return {
                                ...column,
                                width: widths.note,
                                disableSortBy: true,
                                disableFilters: true,
                                canResize: false,
                                Filter: () => <></>,
                                Cell: NotesCell,
                            }
                        }
                        if (column.id === 'user') {
                            return {
                                ...column,
                                width: 250,
                                canResize: false,
                                Cell: ({
                                    row,
                                }: Cell<
                                    ColumnMapType & { userFullName: string }
                                >) => {
                                    return (
                                        <HoverTooltip
                                            text={row.original.userFullName}
                                        >
                                            <Typography component="div">
                                                {row.original.user}
                                            </Typography>
                                        </HoverTooltip>
                                    )
                                },
                            }
                        }

                        if (column.id === 'timeStamp') {
                            return {
                                ...column,
                                canResize: false,
                                width: widths[column.id as keyof typeof widths],
                                Cell: ({
                                    row,
                                }: Cell<
                                    ColumnMapType & { userFullName: string }
                                >) => {
                                    return (
                                        <Typography component="div">
                                            {new Date(
                                                row.original.timeStamp
                                            ).toLocaleDateString()}
                                        </Typography>
                                    )
                                },
                            }
                        }

                        if (column.id === 'reason') {
                            return {
                                ...column,
                                canResize: false,
                                width: widths[column.id as keyof typeof widths],
                                Filter: CustomFilterSelect({
                                    options: reasonObjects,
                                    groupBy: (option) => option.noteType ?? '',
                                    getOptionLabel: (option) =>
                                        option.reason ?? '',
                                    getOptionSelected: (option, value) =>
                                        option?.noteType === value?.noteType &&
                                        option?.reason === value?.reason,
                                    noLabel: true,
                                    wrapperStyle: {
                                        height: '3.65rem',
                                        display: 'flex',
                                        alignItems: 'end',
                                    },
                                    style: {
                                        width: '200px',
                                        maxHeight: pxToRem(80),
                                    },
                                    PaperComponent: CustomPaper,
                                }),
                            }
                        }

                        if (widths[column.id as keyof typeof widths]) {
                            return renderColumn({
                                ...column,
                                canResize: false,
                                width: widths[column.id as keyof typeof widths],
                            })
                        }

                        return renderColumn(column)
                    }),
            ])
        }
    }, [widths, reasonObjects, productHistoryColumns, CustomFilterSelect])

    useEffect(() => {
        updateList()
    }, [filters, sortBy, productHistory])

    useEffect(() => {
        calculateWidths(
            divContainer?.current?.getBoundingClientRect().width ?? 0
        )
    }, [divContainer?.current?.getBoundingClientRect().width])

    useEffect(() => {
        fetchData()
    }, [])

    return (
        <GridContainer data-testid="productHistoryTable">
            <GridItem xs={12} ref={divContainer}>
                <SpecTable
                    {...specTableProps}
                    showTVSettings={true}
                    disableResizeColumns={true}
                    sortBy={prepareOrderByString(sortBy)}
                    inverseColumns={['isActive']}
                    bodyCellCssOverride={{ boxShadow: 'none' }}
                />
            </GridItem>
            <ListingNotesModal
                isOnEditMode={false}
                open={isNotesDialogOpen && !pdnLoading}
                onClose={handleCloseNotesModal}
                isManufacturerListing={
                    listingLevel === ListingLevel.Manufacturer
                }
                selectedNotes={selectedNotes}
                hideFilters={true}
            />
        </GridContainer>
    )
}

export default ProductHistoryTable
